// hooks/useTabManager.ts
import { useState, useEffect, useRef } from "react";

interface UseTabManagerReturn {
  isActive: boolean;
  activateTab: () => void;
}

function generateSecureCode(length = 9) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);

  return Array.from(array, x => chars[x % chars.length]).join("");
}

export const useTabManager = (): UseTabManagerReturn => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const tabIdRef = useRef<string>(`tab_${generateSecureCode()}`);

  const activateTab = (): void => {
    localStorage.setItem("activeTab", tabIdRef.current);
    sessionStorage.setItem("isActiveTab", tabIdRef.current);
    setIsActive(true);

    console.log(`🟢 Tab ${tabIdRef.current.slice(-4)} is taking control`);

    // Il TAB2 manda un comando esplicito al TAB1: "metti in pausa il tuo video"
    localStorage.setItem("pauseCommandMultitab", JSON.stringify({
      fromTabId: tabIdRef.current,
      timestamp: Date.now(),
      action: "pause"
    }));
  };

  useEffect(() => {
    activateTab();

    // Listener esistente per visibilitychange
    document.addEventListener("visibilitychange", () => {
      const activeTab = localStorage.getItem("activeTab");
      const isActiveTab = sessionStorage.getItem("isActiveTab");
      if (!document.hidden) {
        if (activeTab && activeTab !== isActiveTab) {
          setIsActive(false);
        }
      }
    });

    // Listener per comandi espliciti da altri tab
    const handleStorageChange = (event: StorageEvent) => {
      // Reagisce solo al comando esplicito "pauseCommandMultitab"
      if (event.key === "pauseCommandMultitab") {
        try {
          const command = JSON.parse(event.newValue || "{}");

          // Se il comando non proviene da questo tab
          if (command.fromTabId !== tabIdRef.current) {
            console.log(`📨 Tab ${tabIdRef.current.slice(-4)} received pause command from tab ${command.fromTabId?.slice(-4)}`);

            // Emetto evento per far pausare i video
            const pauseEvent = new CustomEvent("pauseVideoFromNewTab", {
              detail: {
                fromTabId: command.fromTabId,
                action: command.action,
                timestamp: command.timestamp
              }
            });
            window.dispatchEvent(pauseEvent);

            setIsActive(false);
          }
        } catch (error) {
          console.error("Errore parsing pauseCommandMultitab:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return {
    activateTab,
    isActive
  };
};
